import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useWorkers } from "@/domains/workers/hooks/use-workers";
import {
  APPOINTMENT_QUERY,
  APPOINTMENTS_QUERY,
  OWNER_CANCEL_APPOINTMENT_MUTATION,
  UPDATE_APPOINTMENT_MUTATION,
} from "@/graphql/appointments";
import { CUSTOMER_QUERY } from "@/graphql/customers";
import { useSalonStore } from "@/lib/store/salon";
import { useUpdateAppointmentServiceWorker } from "../../shared/hooks/use-update-appointment-service-worker";
import { sendWithOverCapacityApproval } from "../../shared/utils/over-capacity-approval";

interface UseAppointmentDetailViewProps {
  appointmentId: string;
}

export function useAppointmentDetailView({ appointmentId }: UseAppointmentDetailViewProps) {
  const { currentSalon } = useSalonStore();
  const [updateAppointment] = useMutation(UPDATE_APPOINTMENT_MUTATION);
  const [ownerCancelAppointment] = useMutation(OWNER_CANCEL_APPOINTMENT_MUTATION);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);

  // Local state for notes to prevent focus loss
  const [localNotes, setLocalNotes] = useState<string>("");
  const [isNotesLoaded, setIsNotesLoaded] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Edit cancellation dialogs
  const [showEditCancellationDialog, setShowEditCancellationDialog] = useState(false);
  const [showEditGuestCancellationDialog, setShowEditGuestCancellationDialog] = useState(false);

  // Fetch workers and appointment data directly
  const { workers } = useWorkers();
  const { updateAppointmentServiceWorker } = useUpdateAppointmentServiceWorker();

  // Query the appointment data directly to ensure it's always fresh
  const { data: appointmentData, loading: appointmentLoading } = useQuery(APPOINTMENT_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      id: appointmentId,
    },
    skip: !currentSalon || !appointmentId,
    fetchPolicy: "cache-and-network",
  });

  // Use the appointment data directly (query uses @unmask)
  const currentAppointment = appointmentData?.appointment ?? undefined;

  // Sync local notes with server data when appointment loads
  useEffect(() => {
    if (currentAppointment && !isNotesLoaded) {
      setLocalNotes(currentAppointment.notes || "");
      setIsNotesLoaded(true);
    }
  }, [currentAppointment, isNotesLoaded]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Query customer details when needed
  const { data: customerData } = useQuery(CUSTOMER_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      customerId: currentAppointment?.customerId || "",
    },
    skip: !currentSalon || !showCustomerDetail || !currentAppointment,
  });

  const handleStatusChange = async (field: "bookingStatus" | "outcome", value: string | null) => {
    if (!currentSalon || !currentAppointment) return;

    setIsUpdating(true);
    try {
      const updateData: Record<string, string | null> = { [field]: value };
      const salonId = currentSalon.id;
      const updatedAppointmentId = currentAppointment.id;

      const result = await sendWithOverCapacityApproval((acknowledgement) =>
        updateAppointment({
          variables: {
            salonId,
            appointmentId: updatedAppointmentId,
            input: { ...updateData, ...acknowledgement },
          },
          // optimisticResponse: {
          //   updateAppointment: {
          //     ...currentAppointment,
          //     [field]: value,
          //   },
          // },
        })
      );
      if (!result) {
        return;
      }

      // Check for GraphQL errors in the result
      if (result.errors && result.errors.length > 0) {
        console.error("Failed to update appointment:", result.errors);
        toast.error(result.errors[0].message || "Failed to update appointment");
        return;
      }

      // Apollo Client cache will automatically update
      toast.success("Appointment updated successfully");
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePaymentMethodChange = async (value: string | null) => {
    if (!currentSalon || !currentAppointment) return;

    setIsUpdating(true);
    try {
      const result = await updateAppointment({
        variables: {
          salonId: currentSalon.id,
          appointmentId: currentAppointment.id,
          input: { paymentMethod: value as "CASH" | "BANKING" | null | undefined },
        },
        refetchQueries: [
          {
            query: APPOINTMENTS_QUERY,
            variables: {
              salonId: currentSalon.id,
              filters: { customerId: currentAppointment.customerId },
            },
          },
        ],
      });

      if (result.errors && result.errors.length > 0) {
        console.error("Failed to update appointment:", result.errors);
        toast.error(result.errors[0].message || "Failed to update appointment");
        return;
      }

      toast.success("Appointment updated successfully");
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeclineConfirm = async (
    cancellationReasonType: string,
    cancellationReasonText: string
  ) => {
    if (!currentSalon || !currentAppointment) return;

    setIsUpdating(true);
    try {
      const result = await ownerCancelAppointment({
        variables: {
          salonId: currentSalon.id,
          appointmentId: currentAppointment.id,
          input: {
            cancellationReasonType: cancellationReasonType as any,
            cancellationReasonText: cancellationReasonText || null,
          },
        },
      });

      // Check for GraphQL errors in the result
      if (result.errors && result.errors.length > 0) {
        console.error("Failed to decline appointment:", result.errors);
        toast.error(result.errors[0].message || "Failed to decline appointment");
        return;
      }

      setShowDeclineDialog(false);
      toast.success("Appointment declined successfully");
    } catch (error) {
      console.error("Failed to decline appointment:", error);
      toast.error("Failed to decline appointment");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleServiceWorkerChange = async (serviceId: string, workerId: string | null) => {
    if (!currentAppointment) return;

    try {
      await updateAppointmentServiceWorker(currentAppointment.id, serviceId, workerId);
    } catch (error) {
      console.error("Failed to update service worker:", error);
    }
  };

  // Debounced function to save notes to server
  const debouncedSaveNotes = useCallback(
    async (notes: string) => {
      if (!currentSalon || !currentAppointment) return;

      try {
        await updateAppointment({
          variables: {
            salonId: currentSalon.id,
            appointmentId: currentAppointment.id,
            input: { notes },
          },
          optimisticResponse: {
            updateAppointment: {
              ...currentAppointment,
              notes,
            },
          },
        });

        // Success is silent for auto-save to avoid spam
      } catch (error) {
        console.error("Failed to auto-save notes:", error);
        toast.error("Your notes changes could not be saved");
      }
    },
    [currentSalon, currentAppointment, updateAppointment]
  );

  const handleEditCancellationConfirm = async (
    cancellationReasonType: string,
    cancellationReasonText: string
  ) => {
    if (!currentSalon || !currentAppointment) return;

    setIsUpdating(true);
    try {
      const result = await updateAppointment({
        variables: {
          salonId: currentSalon.id,
          appointmentId: currentAppointment.id,
          input: {
            cancellationReasonType: cancellationReasonType as any,
            cancellationReasonText: cancellationReasonText || null,
          },
        },
      });

      // Check for GraphQL errors in the result
      if (result.errors && result.errors.length > 0) {
        console.error("Failed to update cancellation reason:", result.errors);
        toast.error(result.errors[0].message || "Failed to update cancellation reason");
        return;
      }

      setShowEditCancellationDialog(false);
      toast.success("Cancellation reason updated successfully");
    } catch (error) {
      console.error("Failed to update cancellation reason:", error);
      toast.error("Failed to update cancellation reason");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditGuestCancellationConfirm = async (
    guestCancellationReason: string,
    guestCancellationText: string
  ) => {
    if (!currentSalon || !currentAppointment) return;

    setIsUpdating(true);
    try {
      const result = await updateAppointment({
        variables: {
          salonId: currentSalon.id,
          appointmentId: currentAppointment.id,
          input: {
            guestCancellationReason: guestCancellationReason as any,
            guestCancellationText: guestCancellationText || null,
          },
        },
      });

      // Check for GraphQL errors in the result
      if (result.errors && result.errors.length > 0) {
        console.error("Failed to update guest cancellation reason:", result.errors);
        toast.error(result.errors[0].message || "Failed to update guest cancellation reason");
        return;
      }

      setShowEditGuestCancellationDialog(false);
      toast.success("Guest cancellation reason updated successfully");
    } catch (error) {
      console.error("Failed to update guest cancellation reason:", error);
      toast.error("Failed to update guest cancellation reason");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle local notes change with debounced save
  const handleNotesChange = useCallback(
    (notes: string) => {
      // Update local state immediately for responsive UI
      setLocalNotes(notes);

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout for debounced save
      debounceTimeoutRef.current = setTimeout(() => {
        debouncedSaveNotes(notes);
      }, 500); // 500ms debounce delay
    },
    [debouncedSaveNotes]
  );

  return {
    // Data
    currentAppointment,
    customerData,
    workers,
    localNotes,

    // Loading states
    appointmentLoading,
    isUpdating,

    // UI state
    showCustomerDetail,
    setShowCustomerDetail,
    showDeclineDialog,
    setShowDeclineDialog,
    showEditCancellationDialog,
    setShowEditCancellationDialog,
    showEditGuestCancellationDialog,
    setShowEditGuestCancellationDialog,

    // Actions
    handleStatusChange,
    handlePaymentMethodChange,
    handleDeclineConfirm,
    handleEditCancellationConfirm,
    handleEditGuestCancellationConfirm,
    handleServiceWorkerChange,
    handleNotesChange,
  };
}
